import { useEffect } from "react";

export default function usePlatformInteractions(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const cleanups = [];
    const timers = new Set();
    const listen = (target, event, handler, options) => {
      if (!target) return;
      target.addEventListener(event, handler, options);
      cleanups.push(() => target.removeEventListener(event, handler, options));
    };
    const later = (callback, delay) => {
      const id = window.setTimeout(() => { timers.delete(id); callback(); }, delay);
      timers.add(id);
    };

    document.title = "OneStack — Digital Operations, Managed.";
    document.documentElement.style.scrollBehavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
	const oldTitle = "OneStack — Digital Operations, Managed.";
    const oldScroll = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";

  // Premium sticky header: transparent at the top, compact glass header on scroll.
  const siteHeader = root.querySelector(".site-header");

  function updateStickyHeader() {
    if (!siteHeader) return;
    siteHeader.classList.toggle("scrolled", window.scrollY > 24);
  }

  updateStickyHeader();
  listen(window, "scroll", updateStickyHeader, { passive: true });

  const menuBtn = root.querySelector(`#${"mobileMenuBtn"}`);
  const mainNav = root.querySelector(`#${"mainNav"}`);

  listen(menuBtn, "click", () => {
    const open = mainNav.classList.toggle("open");
    menuBtn.setAttribute("aria-expanded", String(open));
  });

  root.querySelectorAll("#mainNav a").forEach(link => {
    listen(link, "click", () => {
      mainNav.classList.remove("open");
      menuBtn?.setAttribute("aria-expanded", "false");
    });
  });

  const revealObserver = new window.IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add("visible");
        revealObserver.unobserve(entry.target);
      }
    });
  }, { threshold: 0.12 });

  root.querySelectorAll(".reveal").forEach(el => revealObserver.observe(el));

  cleanups.push(() => revealObserver.disconnect());

  // WhatsApp demo.
  const waChat = root.querySelector(`#${"waChat"}`);
  root.querySelectorAll("[data-reply]").forEach(btn => {
    listen(btn, "click", () => {
      const type = btn.dataset.reply;
      const outgoing = document.createElement("div");
      outgoing.className = "bubble outgoing";
      outgoing.textContent = type === "availability" ? "Please check availability." : "Show me the payment plan.";
      waChat.appendChild(outgoing);

      later(() => {
        const incoming = document.createElement("div");
        incoming.className = "bubble incoming";
        incoming.textContent = type === "availability"
          ? "A-1204 is currently available. I can start the booking workflow or connect you with an agent."
          : "I can retrieve the configured payment plan and booking amount from the connected system.";
        waChat.appendChild(incoming);
        waChat.scrollTop = waChat.scrollHeight;
      }, 350);
    });
  });

  // Country configuration demo.
  const countrySelect = root.querySelector(`#${"countrySelect"}`);
  const countryInfo = root.querySelector(`#${"countryInfo"}`);
  const countryData = {
    "Pakistan": ["PKR", "Configured locally", "Partner dependent"],
    "UAE": ["AED", "Configured locally", "Partner dependent"],
    "Saudi Arabia": ["SAR", "Configured locally", "Partner dependent"],
    "United Kingdom": ["GBP", "Configured locally", "Partner dependent"],
    "United States": ["USD", "Configured locally", "Partner dependent"],
    "Singapore": ["SGD", "Configured locally", "Partner dependent"],
    "Malaysia": ["MYR", "Configured locally", "Partner dependent"]
  };

  function renderCountry() {
    const item = countryData[countrySelect.value] || ["Local", "Configured locally", "Partner dependent"];
    countryInfo.innerHTML = `
      <div><small>Currency</small><strong>${item[0]}</strong></div>
      <div><small>Service availability</small><strong>${item[1]}</strong></div>
      <div><small>Payments</small><strong>${item[2]}</strong></div>
    `;
  }
  listen(countrySelect, "change", renderCountry);

  renderCountry();

  // Business app tabs.
  const appContent = {
    booking: {
      kicker: "BOOKING MANAGEMENT",
      title: "Manage availability, bookings and payments in one place.",
      description: "Centralize inventory, customer details, payment status, notifications and booking workflows.",
      features: ["Inventory & availability", "Booking creation", "Payment tracking", "Customer notifications"]
    },
    orders: {
      kicker: "ORDER MANAGEMENT",
      title: "Move every order from creation to completion.",
      description: "Track customer orders, payment status, fulfillment milestones and operational exceptions.",
      features: ["Order lifecycle", "Payment status", "Customer updates", "Operational reporting"]
    },
    builder: {
      kicker: "BUILDER MANAGEMENT",
      title: "Connect property inventory, customers and installment workflows.",
      description: "Manage projects, units, dealers, bookings, installments, ledgers and customer communication.",
      features: ["Project inventory", "Dealer access", "Installment schedules", "Customer ledger"]
    },
    crm: {
      kicker: "CRM",
      title: "Give sales and service teams a single customer view.",
      description: "Capture leads, customer activity, tasks, opportunities and conversations in one connected workspace.",
      features: ["Lead management", "Customer timeline", "Tasks & follow-ups", "Conversation context"]
    }
  };

  const appTitle = root.querySelector(`#${"appTitle"}`);
  const appKicker = root.querySelector(`#${"appKicker"}`);
  const appDescription = root.querySelector(`#${"appDescription"}`);
  const appFeatures = root.querySelector(`#${"appFeatures"}`);

  root.querySelectorAll(".app-tab").forEach(tab => {
    listen(tab, "click", () => {
      root.querySelectorAll(".app-tab").forEach(t => t.classList.remove("active"));
      tab.classList.add("active");
      const data = appContent[tab.dataset.app];
      appKicker.textContent = data.kicker;
      appTitle.textContent = data.title;
      appDescription.textContent = data.description;
      appFeatures.innerHTML = data.features.map(f => `<li>${f}</li>`).join("");
    });
  });

  // Pricing tabs.
  root.querySelectorAll(".pricing-tab").forEach(tab => {
    listen(tab, "click", () => {
      root.querySelectorAll(".pricing-tab").forEach(t => t.classList.remove("active"));
      root.querySelectorAll(".pricing-panel").forEach(p => p.classList.remove("active"));
      tab.classList.add("active");
      root.querySelector(`#${`pricing-${tab.dataset.pricing}`}`)?.classList.add("active");
    });
  });

  // Managed service estimate.
  const service = root.querySelector(`#${"managedService"}`);
  const hours = root.querySelector(`#${"hoursRange"}`);
  const coverage = root.querySelector(`#${"coverageFactor"}`);
  const sla = root.querySelector(`#${"slaFactor"}`);
  const hoursValue = root.querySelector(`#${"hoursValue"}`);
  const estimatePrice = root.querySelector(`#${"estimatePrice"}`);

  function calculateEstimate() {
    const base = Number(service.value);
    const h = Number(hours.value);
    const coverageFactor = Number(coverage.value);
    const slaFactor = Number(sla.value);
    const normalized = base * (h / 10) * coverageFactor * slaFactor;
    hoursValue.textContent = `${h} hours`;
    estimatePrice.textContent = `$${Math.round(normalized).toLocaleString()}`;
  }

  [service, hours, coverage, sla].forEach(el => listen(el, "input", calculateEstimate));
  calculateEstimate();

  // Demo lead form.
  const leadForm = root.querySelector(`#${"leadForm"}`);
  const formStatus = root.querySelector(`#${"formStatus"}`);
  listen(leadForm, "submit", e => {
    e.preventDefault();
    const data = new FormData(leadForm);
    const company = data.get("company") || "your company";
    formStatus.textContent = `Demo only: your request for ${company} has not been sent. A backend connection is required.`;
    leadForm.reset();
  });

    return () => {
      cleanups.forEach(cleanup => cleanup());
      timers.forEach(id => window.clearTimeout(id));
      document.title = oldTitle;
      document.documentElement.style.scrollBehavior = oldScroll;
    };
  }, [rootRef]);
}
